// Изотермы = контурные линии равной температуры, извлекаемые в БРАУЗЕРЕ из
// сетки SST методом marching squares. Считаем на клиенте намеренно: ползунок
// градуса меняет только УРОВЕНЬ, а сетка та же — перерисовка изотермы не должна
// ходить в сеть. Один запрос грузит поле на вьюпорт, дальше движение ползунка —
// чистый пересчёт по кэшу, мгновенно.
//
// Один уровень за раз (по замыслу: пользователь выбирает конкретный градус).
// Выдаём одну Feature<MultiLineString>: каждый пересекающий ячейку отрезок —
// отдельная 2-точечная линия; с round-стыками они читаются как одна кривая.

export interface SstGrid {
  /** Широта центра строки 0 (самой северной). */
  latTop: number
  /** Долгота центра столбца 0 (самого западного). */
  lonLeft: number
  /** Шаг сетки в градусах (общий по осям) — совместимость со старым ответом. */
  step: number
  /** Шаг по широте, градусы. Без него берётся `step`.
   *
   *  Оси разделены с 14.08.2026: geo9 идёт 1/12° по обеим, и это приучило
   *  описывать ячейку одним числом, а балтийская миля — 1/60° по широте и
   *  1/36° по долготе. Общий шаг растянул бы линию по долготе почти вдвое:
   *  гладко, правдоподобно и не туда. */
  stepLat?: number
  /** Шаг по долготе, градусы. Без него берётся `step`. */
  stepLon?: number
  rows: number
  cols: number
  /** Значения по строкам: `values[i][j]`, строка 0 — север. `null` = нет данных. */
  values: (number | null)[][]
}

type Pt = [number, number] // [lon, lat]

/** Линейная доля, на которой уровень пересекает ребро между значениями a и b. */
function frac(a: number, b: number, level: number): number {
  const d = b - a
  // a === b обрабатывается вызывающим (ребро не пересекается), но подстрахуемся:
  return d === 0 ? 0.5 : (level - a) / d
}

/**
 * Контур уровня `level` по сетке.
 *
 * Классические 16 случаев marching squares. Углы ячейки (по часовой от
 * северо-запада): TL(i,j) TR(i,j+1) BR(i+1,j+1) BL(i+1,j). Рёбра: 0=верх(TL-TR),
 * 1=право(TR-BR), 2=низ(BR-BL), 3=лево(BL-TL). «Внутри» = значение ≥ level;
 * пересекается ребро, у которого один конец внутри, другой снаружи.
 */
export function contourAtLevel(
  gridData: SstGrid,
  level: number,
): GeoJSON.Feature<GeoJSON.MultiLineString> {
  const { values, step, latTop, lonLeft, rows, cols } = gridData
  const stepLat = gridData.stepLat ?? step
  const stepLon = gridData.stepLon ?? step
  const segments: [Pt, Pt][] = []

  // Точка пересечения на ребре между двумя углами сетки (индексы ячеек).
  const lon = (j: number) => lonLeft + j * stepLon
  const lat = (i: number) => latTop - i * stepLat

  // Пересечение по каждому из 4 рёбер как [lon,lat] или null.
  const crossTop = (i: number, j: number, a: number, b: number): Pt =>
    [lon(j) + frac(a, b, level) * stepLon, lat(i)]
  const crossRight = (i: number, j: number, a: number, b: number): Pt =>
    [lon(j + 1), lat(i) - frac(a, b, level) * stepLat]
  const crossBottom = (i: number, j: number, a: number, b: number): Pt =>
    [lon(j) + frac(a, b, level) * stepLon, lat(i + 1)]
  const crossLeft = (i: number, j: number, a: number, b: number): Pt =>
    [lon(j), lat(i) - frac(a, b, level) * stepLat]

  for (let i = 0; i < rows - 1; i++) {
    for (let j = 0; j < cols - 1; j++) {
      const tl = values[i][j]
      const tr = values[i][j + 1]
      const br = values[i + 1][j + 1]
      const bl = values[i + 1][j]
      // Любая дыра в углу — ячейку не контурим: честный разрыв, а не выдумка.
      if (tl == null || tr == null || br == null || bl == null) continue

      // 4-битный код: какие углы «внутри» (≥ level). Порядок бит: TL TR BR BL.
      const code =
        (tl >= level ? 8 : 0) |
        (tr >= level ? 4 : 0) |
        (br >= level ? 2 : 0) |
        (bl >= level ? 1 : 0)
      if (code === 0 || code === 15) continue // ячейка целиком снаружи/внутри

      // Каждое ребро параметризуем в ЕДИНОМ направлении со своим противолежащим:
      // верх/низ — запад→восток (j→j+1), лево/право — север→юг (i→i+1). Иначе
      // frac считался бы от чужого конца и точка съезжала бы на зеркальную долю.
      const top = () => crossTop(i, j, tl, tr)
      const right = () => crossRight(i, j, tr, br)
      const bottom = () => crossBottom(i, j, bl, br)
      const left = () => crossLeft(i, j, tl, bl)

      // Пары рёбер по коду. Сёдла (5, 10) разрешаем по среднему значению углов:
      // ядро выше уровня → углы соединяются «через» центр.
      switch (code) {
        case 1: case 14: segments.push([left(), bottom()]); break
        case 2: case 13: segments.push([bottom(), right()]); break
        case 3: case 12: segments.push([left(), right()]); break
        case 4: case 11: segments.push([top(), right()]); break
        case 6: case 9: segments.push([top(), bottom()]); break
        case 7: case 8: segments.push([left(), top()]); break
        case 5: { // TR+BL внутри, TL+BR снаружи (0b0101)
          if ((tl + tr + br + bl) / 4 >= level) {
            segments.push([left(), top()])
            segments.push([bottom(), right()])
          } else {
            segments.push([left(), bottom()])
            segments.push([top(), right()])
          }
          break
        }
        case 10: { // TL+BR внутри, TR+BL снаружи (0b1010)
          if ((tl + tr + br + bl) / 4 >= level) {
            segments.push([top(), right()])
            segments.push([left(), bottom()])
          } else {
            segments.push([left(), top()])
            segments.push([bottom(), right()])
          }
          break
        }
      }
    }
  }

  // Отбросить вырожденные (нулевой длины) отрезки: уровень ровно на узле даёт
  // совпавшие концы, MapLibre на них ругаться не станет, но и рисовать нечего.
  const coordinates = segments.filter(
    ([a, b]) => a[0] !== b[0] || a[1] !== b[1],
  )

  return {
    type: 'Feature',
    geometry: { type: 'MultiLineString', coordinates },
    properties: { temp: level },
  }
}
